import React, { useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isArray } from 'lodash';

import { WishListContext } from 'Context/Wishlist';

// COMPONENTS
import CartItemValue from '../CartItemValue/CartItemValue';
import CartItemDetails from '../CartItemDetails/CartItemDetails';
import CartItemCounter from '../CartItemCounter/CartItemCounter';
import CartItemActionButtons from '../CartItemActionButtons/CartItemActionButtons';

// DS
import {
  Col,
  Divider,
  Row,
  VertLoading,
  VertTypography,
} from '@vertem-core/vertice-ds';



// STYLES
import 'Assets/styles/default/components/Cart/CartItemListStyles.less';

// UTILS
import { getObjectByKey } from 'Utils/getObjectByKey';
import { isObjectEmpty } from 'Utils';
import { pointsMask } from 'Utils/commons/points';
import ynToBool from 'Utils/ynToBool';
import ToastAlert from 'components/ToastAlert/ToastAlert';
import CartItemProvider from "components/Cart/CartContent/CartItemList/CartItemProvider";
import CouponErrorSummary from 'components/Cart/CartCouponDiscount/CouponErrorSummary';
import { WarningOutlined } from '@ant-design/icons';
import { fetchCartUnavailableThunk } from "redux/Reducers/Cart/fetchCartUnavailableSlicer";

const CartItemMobile = ({ groupedData, setHasItemUnavailable }) => {
  /* Hooks */
  const { simpleWishList } = useContext(WishListContext);

  const {
    errorCart
  } = useSelector(state => state.Cart)

  // REDUCER
  const { errorListSkuUnavailable, dataListSkuUnavailable } = useSelector((state) => state.FetchCartUnavailable)
  const Catalog = useSelector(({ Catalog: data }) => data);
  const itemsCartReducer = useSelector(({ Cart: data }) => data);
  const { coupon, errorSkus: errorSkuToAddCart, errorSkusCart } = itemsCartReducer;
  const couponIsValid = !coupon || coupon.isValid

  const handleUnvaibleProductByCoupon = (sku) => {
    const isSkuInErrorList = (errorList) => errorList && errorList.length > 0 && errorList.includes(sku);

    if (isSkuInErrorList(errorSkusCart)) {
      return true;
    }
    if (isSkuInErrorList(errorSkuToAddCart)) {
      return true;
    }

    return false;
  };


  const ShippingCart = useSelector(({ Shipping: data }) => data);
  const Stores = useSelector(({ Stores: data }) => data);
  const Participants = useSelector(({ Participants: data }) => data);

  /* States */
  const [errorQuantity, setErrorQuantity] = useState({});
  const [unavailableItems, setUnavailableItems] = useState({});

  const handleErrorQuantity = (obj) => {
    setErrorQuantity(obj);
  };

  /* Vars */
  const isLoading =
    ynToBool(itemsCartReducer?.isLoading) || ynToBool(ShippingCart?.isLoading);
  const unavailableProductList =
    ShippingCart?.error && !isObjectEmpty(ShippingCart?.error)
      ? ShippingCart.error.subSkus
      : [];
  const {
    currencyConfiguration: { decimalPlaces, pointsName },
  } = Catalog;
  const { cart } = itemsCartReducer;

  const cartItems = cart?.items || [];
  const {
    value: { vendors: shippingVendors },
  } = ShippingCart;
  const shippingError =
    ShippingCart && ShippingCart?.error && ShippingCart.error.subs;
  const cartError = itemsCartReducer && itemsCartReducer?.error;
  const hasShippingError = !!shippingError;
  const hasCartError = !!cartError;
  const dispatch = useDispatch()

  useEffect(() => {
    handleCartUpdates();
  }, [cartItems, errorCart, dataListSkuUnavailable, errorListSkuUnavailable?.length]);
  
  const verifySkuUnavailable = (errorSkus) => {
    const unavailableCartItems = cartItems.filter((item) =>
      errorSkus.includes(item.sku)
    );
  
    if (unavailableCartItems) {
      setHasItemUnavailable(true);
      
      const unavailableSkus = unavailableCartItems.reduce((acc, item) => {
        acc[item.sku] = true;
        return acc; 
      }, {});

      setUnavailableItems(unavailableSkus) 
    }
  };

  const handleCartUpdates = async () => {
    if (cartItems?.length > 0) {
      await dispatch(fetchCartUnavailableThunk(Participants?.address?.zipCode?.value));
  
      if (dataListSkuUnavailable === true) {
        clearSkuUnavailable();
        return;
      }
  
      if (errorListSkuUnavailable?.length > 0) {
        verifySkuUnavailable(errorListSkuUnavailable);
      }
    }
  };
  
  const clearSkuUnavailable = () => {
    setUnavailableItems({});
    setHasItemUnavailable(false);
  };

  return Object.keys(groupedData).map((vendorId) => {
    let shippingPriceByVendor = {};
    if (
      (!ShippingCart?.error || isObjectEmpty(ShippingCart?.error)) &&
      !ShippingCart?.isLoading
    ) {
      if (isArray(shippingVendors) && shippingVendors?.length > 0) {
        const vendorDataArray = shippingVendors.map((vendor) => ({
          [vendor.id]: {
            deliveryDate: vendor.deliveryDate,
            total: vendor.total,
            totalCash: vendor.totalCash,
          },
        }));

        const vendorArray = vendorDataArray.reduce((acc, vendor) => {
          const vendorId = Object.keys(vendor)[0];
          acc[vendorId] = vendor[vendorId];
          return acc;
        }, {});
        shippingPriceByVendor = getObjectByKey(vendorArray, vendorId);
      }
    }
    const totalShipping = shippingPriceByVendor && shippingPriceByVendor?.total;
    const hasShippingValue = totalShipping === 0 || totalShipping > 0;
    const isFreeShipping = hasShippingValue && totalShipping === 0;

    const dataStore = [...Stores.ecommerce, ...Stores.vouchers];

    const vendorItems = groupedData[vendorId];
    const vendorEcommerce = dataStore.find(
      (store) => store.supplierId === Number(vendorId)
    );

    const vendorVoucher = dataStore.find(
      (store) => store.supplierId === Number(vendorId)
    );

    const hasVoucher = cartItems.some(
      (item) =>
        item.vendor.type == 'PHYSICALVOUCHER' ||
        item.vendor.type == 'EXTERNALVOUCHERVIRTUAL'
    );

    const nameVendor = vendorEcommerce && vendorEcommerce?.name;
    const nameVendorVoucher = vendorVoucher && vendorVoucher?.name;

    const formattedTotalShipping = pointsMask(
      totalShipping,
      decimalPlaces,
      pointsName
    );

    return (
      <>
        <Col className="cart-item-shipping-divider" />
        <Col
          key={vendorId}
          className="cart-item-shipping col-12 vert-bg-light-lighter"
          shape="round"
          verticalSpacing="extra-small"
          spacing="small"
        >
          <VertTypography.Paragraph className="cart-vendor-name">
            Fornecido e entregue por{' '}
            <span>{hasVoucher ? nameVendorVoucher : nameVendor}</span>
          </VertTypography.Paragraph>
          {!!(
            ((!hasCartError || !hasShippingError) &&
              Participants?.address?.zipCode?.value) ||
            hasShippingValue
          ) && (
              <VertTypography.Paragraph className="vendor-shipping-value"
                style={{
                  display: ynToBool(process.env.REACT_APP_HIDE_FREIGHT) ? 'none' : ''
                }}
              >
                Frete: {isLoading && <VertLoading size="small" />}
                {!isLoading && !hasShippingValue && <VertLoading size="small" />}
                {!isLoading && hasShippingValue ? (
                  isFreeShipping ? (
                    <span className="highlight">Grátis</span>
                  ) : (
                    <span>{`${formattedTotalShipping.value} ${formattedTotalShipping.label}`}</span>
                  )
                ) : (
                  ''
                )}
              </VertTypography.Paragraph>
            )}
        </Col>

        {vendorItems.map((item, index) => {
          const skuUnavailable = unavailableItems[item.sku];
          const totalValue = parseFloat(item.price) * parseFloat(item.quantity);
          const isInWishList = simpleWishList.get(item.sku);
          const isUnavailable = unavailableProductList.includes(item.sku);
          const vendorId = item?.vendor?.id;
          const couponUnavailableProduct = handleUnvaibleProductByCoupon(item.sku)
          const filterLastItem = index < (vendorItems.length - 1)
          return (
            <CartItemProvider item={{
              ...item,
              vendor: {
                name: hasVoucher ? nameVendorVoucher : nameVendor
              }
            }}>
              <Col className="cart-item-wrapper">

                {
                  couponUnavailableProduct && (
                    <div className='cart-item-coupon-alert'>
                      <CouponErrorSummary error='Este produto não é valido para o cupom aplicado.' type={couponIsValid ? 'warning' : 'error'} />
                    </div>
                  )
                }
                <CartItemDetails
                  imageAlt={item.name}
                  imageUrl={item.imageUrl}
                  itemName={item.name}
                  itemSeller={nameVendor}
                  shippingValue={null}
                  isFavorited={isInWishList}
                  hideActionsButtons
                  isUnavailable={isUnavailable}
                  skuUnavailable={skuUnavailable}
                />
                <Row justify="space-between" align="middle">
                  <Row className="action-buttons-wrapper" align="middle">
                    <div
                      style={{
                        width: '180px',
                        margin: '20px 0',
                      }}
                    >
                      <CartItemCounter
                        quantity={item.quantity}
                        sku={item.sku}
                        vendorId={vendorId}
                        setErrorQuantity={handleErrorQuantity}
                        errorQuantity={errorQuantity}
                      />
                    </div>
                    <CartItemActionButtons
                      isFavorited={isInWishList}
                      isLoading={isLoading}
                      onlyIcons
                      sku={item.sku}
                    />
                  </Row>
                  <CartItemValue value={totalValue} />
                  {errorQuantity[item.sku] && errorQuantity[item.sku] !== '' ? (
                    <ToastAlert
                      className={'margin-top-small'}
                      closable={false}
                      size={'small'}
                      message={errorQuantity[item.sku]}
                    />
                  ) : (
                    ''
                  )}
                  <Row>
                  {skuUnavailable && (
                    <div className='unavailable-container'>
                      <div className='unavailable-content'>
                        <WarningOutlined className="icon-unavailable"/>
                        <span>Produto Indisponível</span>
                      </div>
                    </div>
                    )}
                  </Row>
                </Row>
                {filterLastItem && <Divider/>}
              </Col>
            </CartItemProvider>
          );
        })}
      </>
    );
  });

}

export default CartItemMobile